import Chart from "react-apexcharts";
import Card from "components/card";
import { ReskillingInforme } from "services/reskillingService";

/**
 * Informe de competencias docentes.
 *
 * A diferencia del de competencias generales, este compara lo que la persona
 * cree de sí misma (autopercepción) con lo que mostró al responder
 * (desempeño). La brecha entre ambas es lo que más dice del perfil.
 */
export default function InformeDocente({
  informe,
}: {
  informe: ReskillingInforme;
}) {
  const docente = informe.docente;

  if (!docente) {
    return (
      <Card extra="p-6">
        <p className="text-sm text-gray-500">
          Este informe todavía no tiene resultados.
        </p>
      </Card>
    );
  }

  const tramos = docente.tramos ?? [];
  const niveles = docente.niveles ?? [];

  return (
    <div className="space-y-5">
      {/* Resumen por tramo */}
      {tramos.length > 0 && (
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          {tramos.map((t) => (
            <Card key={t.tramo} extra="p-6">
              <h3 className="mb-4 text-base font-bold text-navy-700 dark:text-white">
                {t.nombre}
              </h3>

              <div className="mb-3 flex items-end gap-6">
                <Medida
                  etiqueta="Autopercepción"
                  valor={t.autopercepcion}
                  color="#7c3aed"
                />
                <Medida
                  etiqueta="Desempeño"
                  valor={t.desempeno}
                  color="#0d9488"
                />
              </div>

              <p className="text-xs text-gray-500">
                {t.completos} de {t.niveles} niveles completados
              </p>
            </Card>
          ))}
        </div>
      )}

      {/* Comparación por nivel */}
      {niveles.length > 0 && (
        <Card extra="p-6">
          <h3 className="mb-1 text-lg font-bold text-navy-700 dark:text-white">
            Autopercepción y desempeño por nivel
          </h3>
          <p className="mb-4 text-sm text-gray-500">
            La distancia entre ambas barras muestra qué tan calibrada está la
            percepción de la persona sobre su propio desempeño.
          </p>

          <Chart
            type="bar"
            height={Math.max(260, niveles.length * 62)}
            series={[
              {
                name: "Autopercepción",
                data: niveles.map((n) => n.autopercepcion?.puntaje ?? 0),
              },
              {
                name: "Desempeño",
                data: niveles.map((n) => n.desempeno?.puntaje_100 ?? 0),
              },
            ]}
            options={{
              chart: { toolbar: { show: false }, fontFamily: "inherit" },
              colors: ["#7c3aed", "#0d9488"],
              plotOptions: {
                bar: { horizontal: true, borderRadius: 4, barHeight: "70%" },
              },
              dataLabels: { enabled: false },
              xaxis: { max: 100, labels: { formatter: (v) => `${v}` } },
              yaxis: {
                labels: {
                  formatter: (v) => String(v),
                  style: { fontSize: "12px" },
                },
              },
              labels: niveles.map((n) => n.sigla || n.nombre),
              legend: { position: "top", horizontalAlign: "left" },
              grid: { borderColor: "#e2e8f0" },
            }}
          />
        </Card>
      )}

      {/* Detalle de cada nivel */}
      <div className="space-y-4">
        {niveles.map((n) => (
          <Card key={n.sigla || n.orden} extra="p-6">
            <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
              <div>
                <h4 className="font-bold text-navy-700 dark:text-white">
                  {n.nombre}
                </h4>
                <p className="text-xs uppercase text-gray-400">{n.tramo}</p>
              </div>

              {n.gap && <EtiquetaGap gap={n.gap} />}
            </div>

            <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Barra
                etiqueta="Autopercepción"
                valor={n.autopercepcion?.puntaje ?? 0}
                banda={n.autopercepcion?.banda}
                color="#7c3aed"
              />
              <Barra
                etiqueta="Desempeño"
                valor={n.desempeno?.puntaje_100 ?? 0}
                banda={
                  n.desempeno?.estado === "no_evaluable"
                    ? "No evaluable"
                    : n.desempeno?.banda
                }
                color="#0d9488"
              />
            </div>

            {n.gap?.texto && (
              <p className="mb-3 text-sm leading-relaxed text-gray-600 dark:text-gray-300">
                {n.gap.texto}
              </p>
            )}

            {Array.isArray(n.desempeno?.indicadores) &&
              n.desempeno!.indicadores!.length > 0 && (
                <div>
                  <p className="mb-2 text-xs font-semibold uppercase text-gray-500">
                    Indicadores observados
                  </p>
                  <div className="space-y-1">
                    {n.desempeno!.indicadores!.map((ind) => (
                      <div
                        key={ind.clave}
                        className="flex items-center justify-between text-sm"
                      >
                        <span className="text-gray-600 dark:text-gray-300">
                          <strong>{ind.letra}</strong> · {ind.nombre}
                        </span>
                        <Indicador valor={ind.valor} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
          </Card>
        ))}
      </div>
    </div>
  );
}

function Medida({
  etiqueta,
  valor,
  color,
}: {
  etiqueta: string;
  valor?: number;
  color: string;
}) {
  return (
    <div>
      <p className="text-xs uppercase text-gray-500">{etiqueta}</p>
      <p className="text-2xl font-bold" style={{ color }}>
        {typeof valor === "number" ? Math.round(valor) : "—"}
      </p>
    </div>
  );
}

function Barra({
  etiqueta,
  valor,
  banda,
  color,
}: {
  etiqueta: string;
  valor: number;
  banda?: string;
  color: string;
}) {
  return (
    <div>
      <div className="mb-1 flex items-center justify-between text-sm">
        <span className="text-gray-600 dark:text-gray-300">{etiqueta}</span>
        <span className="font-semibold" style={{ color }}>
          {valor}
        </span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-navy-700">
        <div
          className="h-full rounded-full"
          style={{ width: `${valor}%`, backgroundColor: color }}
        />
      </div>
      {banda && <p className="mt-1 text-xs text-gray-400">{banda}</p>}
    </div>
  );
}

/** Colores según qué tan lejos está la autopercepción del desempeño. */
const COLORES_GAP: Record<string, string> = {
  cercana: "bg-green-100 text-green-700",
  leve: "bg-yellow-100 text-yellow-700",
  moderada: "bg-orange-100 text-orange-700",
  alta: "bg-red-100 text-red-700",
};

function EtiquetaGap({
  gap,
}: {
  gap: { valor?: number; banda?: string; banda_clave?: string; sentido?: string };
}) {
  const clase = COLORES_GAP[gap.banda_clave ?? ""] ?? "bg-gray-100 text-gray-600";

  return (
    <div className="text-right">
      <span
        className={`inline-block rounded-full px-3 py-1 text-xs font-medium ${clase}`}
      >
        {gap.banda ?? "—"}
      </span>
      {gap.sentido && (
        <p className="mt-1 text-xs capitalize text-gray-400">{gap.sentido}</p>
      )}
    </div>
  );
}

/** Un indicador vale 0, 1, 2 o "NE" cuando no se pudo observar. */
function Indicador({ valor }: { valor: number | string }) {
  if (valor === "NE") {
    return (
      <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-500">
        No evaluable
      </span>
    );
  }

  const n = Number(valor);
  const colores = ["bg-red-100 text-red-700", "bg-yellow-100 text-yellow-700", "bg-green-100 text-green-700"];

  return (
    <span
      className={`rounded px-2 py-0.5 text-xs font-semibold ${
        colores[n] ?? "bg-gray-100 text-gray-600"
      }`}
    >
      {n} / 2
    </span>
  );
}
